package com.arena_web.pages.clubs

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ClubDetailViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.data.dto.ClubDto
import com.arena_web.data.dto.ClubGalleryImageDto
import com.arena_web.data.dto.ClubPostDto
import com.arena_web.data.dto.ClubReviewDto
import com.arena_web.data.dto.ClubRuleDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.booking.BookCourtPopup
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

private data class AmenityInfo(val type: String, val label: String, val icon: String)

private val ALL_AMENITIES = listOf(
    AmenityInfo("PARKING", "Parking", AppIcons.Parking),
    AmenityInfo("CAFE", "Cafe", AppIcons.Cafe),
    AmenityInfo("WIFI", "WiFi", AppIcons.Wifi),
    AmenityInfo("RECEPTION", "Reception", AppIcons.Reception),
    AmenityInfo("SHOWER", "Shower", AppIcons.Shower),
    AmenityInfo("FIRST_AID", "First aid", AppIcons.FirstAid),
)

@Page("/clubs/{id}")
@Composable
fun ClubDetailPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val clubId = ctx.route.path.substringAfterLast("/")

    val viewModel = rememberViewModel(key = clubId) {
        ClubDetailViewModel(
            clubId = clubId,
            clubPublicRepository = container.clubPublicRepository,
            clubAdminRepository = container.clubAdminRepository
        )
    }

    val t = LocalAppStrings.current
    val state by viewModel.uiState.collectAsState()
    var showBookingPopup by remember { mutableStateOf(false) }

    AppPageLayout(activeTab = SidebarTab.Arena) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(32, 40)
                .sPaddingBottom(30)
                .sGap(24)
        ) {
            // Back button
            Row(
                modifier = Modifier
                    .sGap(8)
                    .cursor(Cursor.Pointer)
                    .onClick { ctx.router.navigateTo(Routes.ARENA) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    src = AppIcons.ChevronForward,
                    modifier = Modifier.styleModifier {
                        property("transform", "rotate(180deg)")
                    }
                )
                SpanText(
                    t.pageBackToEvents,
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                )
            }

            if (state.isLoading) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.pageLoading,
                        modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                    )
                }
            } else if (state.error != null) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        state.error ?: t.pageSomethingWentWrong,
                        modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                    )
                }
            } else {
                // Two-column layout
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .sGap(20)
                        .styleModifier {
                            property("align-items", "stretch")
                        }
                ) {
                    // Left column (~1251px proportional)
                    Column(
                        modifier = Modifier
                            .sGap(20)
                            .fillMaxHeight()
                            .overflow(Overflow.Auto)
                            .styleModifier {
                                property("flex", "1251 1 0px")
                                property("min-width", "0")
                                property("scrollbar-width", "thin")
                                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                            }
                    ) {
                        // Image Gallery
                        ClubDetailImageGallery(
                            club = state.club,
                            gallery = state.gallery,
                            onBookClick = { showBookingPopup = true }
                        )

                        // Bottom section: Court Rules+Amenities | Map | Reviews — ONE row
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sGap(20)
                                .styleModifier {
                                    property("flex", "1")
                                    property("min-height", "0")
                                }
                        ) {
                            // Left stack: Court Rules (top) + Amenities (bottom)
                            Column(
                                modifier = Modifier
                                    .sWidth(440)
                                    .fillMaxHeight()
                                    .sGap(20)
                                    .styleModifier {
                                        property("flex-shrink", "0")
                                    }
                            ) {
                                ClubDetailCourtRules(rules = state.rules)
                                ClubDetailAmenities(amenities = state.amenities)
                            }

                            // Map
                            ClubDetailMapSection(club = state.club)

                            // Reviews list
                            ClubDetailReviews(
                                club = state.club,
                                reviews = state.reviews,
                                onShowReviewForm = { viewModel.showReviewForm() }
                            )
                        }
                    }

                    // Right column: Posts Feed (~569px proportional)
                    ClubDetailPostsFeed(
                        club = state.club,
                        posts = state.posts,
                        isLoadingPosts = state.isLoadingPosts,
                        hasMorePosts = state.hasMorePosts,
                        onLoadMore = { viewModel.loadMorePosts() },
                        onToggleLike = { postId -> viewModel.toggleLike(postId) }
                    )
                }
            }
        }

        // Review Form Popup
        if (state.showReviewForm) {
            ReviewFormPopup(
                clubType = state.club?.type,
                customSportName = state.club?.customSportName,
                isSubmitting = state.isSubmittingReview,
                onClose = { viewModel.hideReviewForm() },
                onSubmit = { rating, comment -> viewModel.submitReview(rating, comment) }
            )
        }

        BookCourtPopup(
            isOpen = showBookingPopup,
            clubId = clubId,
            onClose = { showBookingPopup = false },
            onBooked = { showBookingPopup = false }
        )
    }
}

// ── Image Gallery ──

@Composable
private fun ClubDetailImageGallery(
    club: ClubDto?,
    gallery: List<ClubGalleryImageDto>,
    onBookClick: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    Div(attrs = {
        style {
            property("display", "flex")
            property("gap", s(12))
            property("height", s(342))
            property("flex-shrink", "0")
        }
    }) {
        // Main Banner (wider than right grid)
        Box(
            modifier = Modifier
                .sHeight(342)
                .sBorderRadius(22)
                .styleModifier {
                    property("flex", "1.6")
                    property("min-width", "0")
                    property("position", "relative")
                    property("overflow", "hidden")
                    property(
                        "background",
                        "linear-gradient(201deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                    )
                }
        ) {
            if (!club?.mainPhotoUrl.isNullOrBlank()) {
                Image(
                    src = club!!.mainPhotoUrl!!,
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("object-fit", "cover")
                    }
                )
            }

            // Venue Details Overlay — positioned top-left
            Box(
                modifier = Modifier
                    .sWidth(420)
                    .styleModifier {
                        property("position", "absolute")
                        property("left", s(14))
                        property("top", s(14))
                        property("padding", "${s(18)} ${s(22)}")
                        property("border-radius", s(22))
                        property("background", "rgba(2, 4, 11, 0.4)")
                        property("backdrop-filter", "blur(11px)")
                    }
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().sGap(24)
                ) {
                    // Name + Address
                    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                        SpanText(
                            club?.name ?: "",
                            modifier = Modifier
                                .sFontSize(32)
                                .fontWeight(AppTypography.SemiBold)
                                .sLineHeight(38)
                                .sLetterSpacing(0.32)
                                .color(AppColors.Text.Primary)
                                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                        )
                        Row(modifier = Modifier.sGap(4), verticalAlignment = Alignment.Top) {
                            Icon(
                                src = AppIcons.Location,
                                modifier = Modifier.sSize(24).styleModifier {
                                    property("filter", AppColors.IconDefaultFilter)
                                    property("flex-shrink", "0")
                                    property("margin-top", s(1))
                                }
                            )
                            val fullAddress = club?.address ?: ""
                            val commas = fullAddress.mapIndexedNotNull { i, c -> if (c == ',') i else null }
                            val splitAt = if (commas.size >= 2) commas[1] else -1
                            val street = if (splitAt >= 0) fullAddress.substring(0, splitAt).trim() else fullAddress.trim()
                            val cityCountry = if (splitAt >= 0) fullAddress.substring(splitAt + 1).trim() else ""
                            Column(modifier = Modifier.sGap(2)) {
                                SpanText(
                                    street,
                                    modifier = Modifier.bodyXlRegular().color(AppColors.Text.Primary)
                                )
                                if (cityCountry.isNotBlank()) {
                                    SpanText(
                                        cityCountry,
                                        modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                                    )
                                }
                            }
                        }
                    }

                    // Book button — aligned right
                    val sportColors = LocalSportColors.current
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.End
                    ) {
                        Row(
                            modifier = Modifier
                                .sHeight(44)
                                .sBorderRadius(22)
                                .backgroundColor(sportColors.primary)
                                .cursor(Cursor.Pointer)
                                .onClick { onBookClick() }
                                .sPaddingTop(4)
                                .sPaddingBottom(4)
                                .sPaddingLeft(36)
                                .sPaddingRight(4)
                                .sGap(10),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                t.pageBook,
                                modifier = Modifier.bodyLMedium().color(sportColors.onPrimary)
                                    .styleModifier { property("white-space", "nowrap") }
                            )
                            Box(
                                modifier = Modifier
                                    .sSize(36)
                                    .sBorderRadius(100)
                                    .backgroundColor(sportColors.secondary)
                                    .styleModifier { property("overflow", "hidden") },
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    src = AppIcons.ArrowUpOutline,
                                    modifier = Modifier.sSize(18).styleModifier {
                                        property("transform", "rotate(45deg)")
                                        property("filter", sportColors.iconFilterOnPrimary)
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }

        // Right image grid — adapts to available images
        if (gallery.isNotEmpty()) {
            Div(attrs = {
                style {
                    property("flex", "1")
                    property("min-width", "0")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", s(12))
                }
            }) {
                when (gallery.size) {
                    1 -> {
                        // Single image — full height
                        GalleryImageSlot(
                            imageUrl = gallery[0].imageUrl,
                            modifier = Modifier.fillMaxWidth().styleModifier {
                                property("flex", "1")
                                property("min-height", "0")
                            }
                        )
                    }
                    2 -> {
                        // Two images — stacked vertically
                        GalleryImageSlot(
                            imageUrl = gallery[0].imageUrl,
                            modifier = Modifier.fillMaxWidth().styleModifier {
                                property("flex", "1")
                                property("min-height", "0")
                            }
                        )
                        GalleryImageSlot(
                            imageUrl = gallery[1].imageUrl,
                            modifier = Modifier.fillMaxWidth().styleModifier {
                                property("flex", "1")
                                property("min-height", "0")
                            }
                        )
                    }
                    else -> {
                        // 3+ images — 1 tall + 2 small row
                        GalleryImageSlot(
                            imageUrl = gallery[0].imageUrl,
                            modifier = Modifier.fillMaxWidth().styleModifier {
                                property("flex", "1")
                                property("min-height", "0")
                            }
                        )
                        Div(attrs = {
                            style {
                                property("display", "flex")
                                property("gap", s(12))
                                property("flex", "1")
                                property("min-height", "0")
                            }
                        }) {
                            GalleryImageSlot(
                                imageUrl = gallery[1].imageUrl,
                                modifier = Modifier.styleModifier {
                                    property("flex", "1")
                                    property("min-width", "0")
                                    property("height", "100%")
                                }
                            )
                            GalleryImageSlot(
                                imageUrl = gallery[2].imageUrl,
                                modifier = Modifier.styleModifier {
                                    property("flex", "1")
                                    property("min-width", "0")
                                    property("height", "100%")
                                }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun GalleryImageSlot(
    imageUrl: String?,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .sBorderRadius(22)
            .styleModifier {
                property("overflow", "hidden")
                property(
                    "background",
                    "linear-gradient(190deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        if (!imageUrl.isNullOrBlank()) {
            Image(
                src = imageUrl,
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("object-fit", "cover")
                }
            )
        }
    }
}

// ── Court Rules ──

@Composable
private fun ClubDetailCourtRules(rules: List<ClubRuleDto>) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sBorderRadius(22)
            .styleModifier {
                property("flex", "1.4")
                property("min-height", "0")
                property("width", "100%")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("overflow", "hidden")
            }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(16, 18)
                .sGap(22)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.pageCourtRules,
                    modifier = Modifier
                        .h2Medium()
                        .color(AppColors.Text.Secondary)
                        .styleModifier { property("flex", "1") }
                )
                Icon(
                    src = AppIcons.Document,
                    modifier = Modifier.sSize(24).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                    }
                )
            }

            // Rules list
            if (rules.isEmpty()) {
                SpanText(
                    t.pageNoCourtRules,
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                )
            } else {
                rules.forEach { rule ->
                    Row(
                        modifier = Modifier.fillMaxWidth().sGap(14),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(
                            modifier = Modifier.sSize(4).sBorderRadius(100)
                                .styleModifier {
                                    property("background", "#0061FF")
                                    property("flex-shrink", "0")
                                }
                        )
                        SpanText(
                            rule.text,
                            modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
                        )
                    }
                }
            }
        }
    }
}

// ── Amenities ──

@Composable
private fun ClubDetailAmenities(amenities: List<String>) {
    // Only the amenities the club actually has (from the public detail endpoint).
    val clubAmenities = ALL_AMENITIES.filter { it.type in amenities }
    if (clubAmenities.isEmpty()) return
    Column(
        modifier = Modifier
            .sBorderRadius(22)
            .sGap(8)
            .styleModifier {
                property("flex", "1")
                property("min-height", "0")
                property("width", "100%")
                property("overflow-y", "auto")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            }
    ) {
        // 2-column grid
        val rows = clubAmenities.chunked(2)
        rows.forEach { row ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(12))
                    width(100.percent)
                }
            }) {
                row.forEach { info ->
                    ServiceChip(name = info.label, icon = info.icon)
                }
                if (row.size == 1) {
                    Box(modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    })
                }
            }
        }
    }
}

@Composable
private fun ServiceChip(name: String, icon: String?) {
    Box(
        modifier = Modifier
            .sHeight(60)
            .sBorderRadius(14)
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("overflow", "hidden")
                property(
                    "background",
                    "linear-gradient(199deg, rgba(60, 121, 241, 0.096) 34%, rgba(5, 4, 12, 0.09) 67%)"
                )
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (icon != null) {
                Icon(
                    src = icon,
                    modifier = Modifier.sSize(24)
                )
            }
            SpanText(
                name,
                modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
            )
        }
    }
}

// ── Map Section ──

@Composable
private fun ClubDetailMapSection(club: ClubDto?) {
    val lat = club?.latitude ?: 0.0
    val lng = club?.longitude ?: 0.0

    Box(
        modifier = Modifier
            .sBorderRadius(22)
            .styleModifier {
                property("flex", "340 1 0px")
                property("min-width", "0")
                property("height", "100%")
                property("overflow", "hidden")
                property("position", "relative")
            }
    ) {
        Div(attrs = {
            id("club-detail-map")
            style {
                width(100.percent)
                height(100.percent)
            }
        })

        LaunchedEffect(lat, lng) {
            delay(200)
            window.asDynamic().__clubDetailMapLat = lat
            window.asDynamic().__clubDetailMapLng = lng

            js("""
                (function() {
                    var id = 'club-detail-map';
                    var lat = window.__clubDetailMapLat;
                    var lng = window.__clubDetailMapLng;
                    var container = document.getElementById(id);
                    if (!container) return;
                    if (typeof L === 'undefined') return;

                    if (window.__clubDetailMap) {
                        try { window.__clubDetailMap.remove(); } catch(e) {}
                        window.__clubDetailMap = null;
                    }

                    container.addEventListener('wheel', function(e) { e.stopPropagation(); }, { passive: false });
                    container.addEventListener('touchmove', function(e) { e.stopPropagation(); }, { passive: false });

                    var map = L.map(id, {
                        zoomControl: false,
                        attributionControl: false,
                        scrollWheelZoom: false,
                        dragging: false,
                        doubleClickZoom: false,
                        touchZoom: false,
                        boxZoom: false
                    }).setView([lat, lng], 15);

                    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                        maxZoom: 20,
                        subdomains: 'abcd'
                    }).addTo(map);

                    var markerIcon = L.divIcon({
                        className: '',
                        html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="26" height="35">' +
                            '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#0061FF"/>' +
                            '<circle cx="12" cy="11" r="5" fill="white"/>' +
                            '</svg>',
                        iconSize: [26, 35],
                        iconAnchor: [13, 35]
                    });

                    L.marker([lat, lng], { icon: markerIcon }).addTo(map);

                    window.__clubDetailMap = map;
                    setTimeout(function() { map.invalidateSize(); }, 150);
                })();
            """)
        }

        DisposableEffect(Unit) {
            onDispose {
                js("""
                    (function() {
                        if (window.__clubDetailMap) {
                            try { window.__clubDetailMap.remove(); } catch(e) {}
                            window.__clubDetailMap = null;
                        }
                    })();
                """)
            }
        }

    }
}

// ── Reviews Section ──

@Composable
private fun ClubDetailReviews(
    club: ClubDto?,
    reviews: List<ClubReviewDto>,
    onShowReviewForm: () -> Unit
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .sBorderRadius(22)
            .fillMaxHeight()
            .styleModifier {
                property("flex", "375 1 0px")
                property("min-width", "0")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("overflow", "hidden")
            }
    ) {
        // Header: Rating + "Leave a Review" button
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sPadding(16)
                .styleModifier { property("flex-shrink", "0") },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Row(modifier = Modifier.sGap(4), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "${club?.averageRating ?: "0.0"}",
                    modifier = Modifier.bodyXlRegular().color(AppColors.Text.Primary)
                )
                Icon(
                    src = AppIcons.StarRating16,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", "brightness(0) saturate(100%) invert(79%) sepia(70%) saturate(500%) hue-rotate(5deg)")
                    }
                )
                SpanText(
                    "(${club?.totalRatingsCount ?: 0})",
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                )
            }

            // Leave a Review button
            Box(
                modifier = Modifier
                    .sHeight(44)
                    .sBorderRadius(22)
                    .sPadding(0, 20)
                    .cursor(Cursor.Pointer)
                    .onClick { onShowReviewForm() }
                    .styleModifier {
                        property("border", "1px solid rgba(46, 96, 217, 0.4)")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, #060E1E, #060E1E) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.pageLeaveReview,
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }

        // Reviews list (scrollable)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .sPadding(0, 16)
                .sPaddingBottom(16)
                .sGap(16)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
        ) {
            if (reviews.isEmpty()) {
                SpanText(
                    t.pageNoReviewsYet,
                    modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                        .sPadding(16)
                )
            } else {
                reviews.forEach { review ->
                    ReviewCard(
                        name = review.userName,
                        date = review.createdAt.substringBefore("T"),
                        rating = review.rating.toInt(),
                        text = review.comment,
                        avatarUrl = review.userAvatarUrl
                    )
                }
            }
        }
    }
}

// ── Review Form Popup ──

@Composable
private fun ReviewFormPopup(
    clubType: String?,
    customSportName: String? = null,
    isSubmitting: Boolean,
    onClose: () -> Unit,
    onSubmit: (Int, String) -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var selectedRating by remember { mutableStateOf(0) }
    var reviewText by remember { mutableStateOf("") }

    // Overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .onClick { onClose() }
            .styleModifier {
                property("background", "rgba(0, 0, 0, 0.6)")
                property("backdrop-filter", "blur(4px)")
            },
        contentAlignment = Alignment.Center
    ) {
        // Popup card
        Column(
            modifier = Modifier
                .sWidth(420)
                .sBorderRadius(18)
                .sPadding(22)
                .sPaddingTop(18)
                .sGap(22)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("background", "#09142d")
                },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Title
            val sportName = when (clubType?.uppercase()) {
                "FOOTBALL" -> t.pageSportFootball
                "BASKETBALL" -> t.pageSportBasketball
                "TENNIS" -> t.pageSportTennis
                "PADEL" -> t.pageSportPadel
                "OTHER" -> customSportName ?: t.pageClubFallback
                else -> t.pageClubFallback
            }
            SpanText(
                t.pageReviewExperienceTitle(sportName),
                modifier = Modifier
                    .h4()
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("text-align", "center") }
            )

            Column(modifier = Modifier.fillMaxWidth().sGap(18)) {
                // Star rating
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(8),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    repeat(5) { index ->
                        val starIndex = index + 1
                        Icon(
                            src = AppIcons.StarRating24,
                            modifier = Modifier
                                .sSize(24)
                                .cursor(Cursor.Pointer)
                                .onClick { selectedRating = starIndex }
                                .styleModifier {
                                    if (starIndex <= selectedRating) {
                                        property("filter", "brightness(0) saturate(100%) invert(79%) sepia(70%) saturate(500%) hue-rotate(5deg)")
                                    } else {
                                        property("filter", AppColors.IconDefaultFilter)
                                        property("opacity", "0.4")
                                    }
                                }
                        )
                    }
                }

                // Text area
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(100)
                        .sBorderRadius(18)
                        .styleModifier {
                            property("border", "0.5px solid ${sportColors.primary}99")
                        }
                ) {
                    org.jetbrains.compose.web.dom.TextArea(value = reviewText) {
                        onInput { reviewText = it.value }
                        style {
                            width(100.percent)
                            height(100.percent)
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("resize", "none")
                            property("color", AppColors.Text.Primary)
                            property("font-family", "'Urbanist', sans-serif")
                            property("font-size", s(14))
                            property("padding", "${s(12)} ${s(12)}")
                            property("scrollbar-width", "thin")
                            property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        }
                        attr("placeholder", t.pageWriteReviewPlaceholder)
                    }
                }

                // Action buttons
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    horizontalArrangement = Arrangement.End
                ) {
                    // Cancel
                    Box(
                        modifier = Modifier
                            .sHeight(44)
                            .sBorderRadius(22)
                            .sPadding(0, 26)
                            .cursor(Cursor.Pointer)
                            .onClick { onClose() }
                            .styleModifier {
                                property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                                property(
                                    "background",
                                    AppGradients.InteractiveSecondary
                                )
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            t.commonCancel,
                            modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                        )
                    }

                    // Post Review
                    Box(
                        modifier = Modifier
                            .sHeight(44)
                            .sBorderRadius(22)
                            .sPadding(0, 26)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                if (selectedRating > 0 && !isSubmitting) {
                                    onSubmit(selectedRating, reviewText)
                                }
                            }
                            .styleModifier {
                                property("background", sportColors.primary)
                                if (selectedRating == 0 || isSubmitting) {
                                    property("opacity", "0.5")
                                }
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            if (isSubmitting) t.pagePosting else t.pagePostReview,
                            modifier = Modifier.bodyLMedium().color(Color.white)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ReviewCard(
    name: String,
    date: String,
    rating: Int,
    text: String? = null,
    avatarUrl: String? = null
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .sPadding(16)
            .sGap(16)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.06) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
            }
    ) {
        // Header: Avatar + Name/Date + Stars
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Avatar + Author info
            Row(
                modifier = Modifier.sGap(12).styleModifier { property("flex", "1") },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(18)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("flex-shrink", "0")
                            property("background", "rgba(60, 121, 241, 0.2)")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    if (!avatarUrl.isNullOrBlank()) {
                        Image(
                            src = avatarUrl,
                            modifier = Modifier.fillMaxSize().styleModifier {
                                property("object-fit", "cover")
                            }
                        )
                    } else {
                        Icon(
                            src = AppIcons.Profile,
                            modifier = Modifier.sSize(18).styleModifier {
                                property("filter", AppColors.IconDefaultFilter)
                            }
                        )
                    }
                }
                Column(modifier = Modifier.sGap(4)) {
                    SpanText(
                        name,
                        modifier = Modifier.bodyXlMedium().color(AppColors.Text.Primary)
                    )
                    SpanText(
                        date,
                        modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                    )
                }
            }

            // Star rating
            Row(modifier = Modifier.sGap(5)) {
                repeat(5) { i ->
                    Icon(
                        src = AppIcons.StarRating16,
                        modifier = Modifier.sSize(16).styleModifier {
                            if (i < rating) {
                                property("filter", "brightness(0) saturate(100%) invert(79%) sepia(70%) saturate(500%) hue-rotate(5deg)")
                            } else {
                                property("filter", AppColors.IconDefaultFilter)
                                property("opacity", "0.3")
                            }
                        }
                    )
                }
            }
        }

        // Review text
        if (!text.isNullOrBlank()) {
            SpanText(
                text,
                modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
            )
        }
    }
}

// ── Posts Feed ──

@Composable
private fun ClubDetailPostsFeed(
    club: ClubDto?,
    posts: List<ClubPostDto>,
    isLoadingPosts: Boolean,
    hasMorePosts: Boolean,
    onLoadMore: () -> Unit,
    onToggleLike: (String) -> Unit
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sBorderRadius(22)
            .styleModifier {
                property("flex", "569 1 0px")
                property("height", "100%")
                property("overflow", "hidden")
                property("min-width", "0")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("-webkit-backdrop-filter", "blur(11px)")
                property("backdrop-filter", "blur(11px)")
            }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(16)
                .sGap(0)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
        ) {
            if (posts.isEmpty() && !isLoadingPosts) {
                // Empty state
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("flex", "1")
                            property("justify-content", "center")
                        }
                        .sGap(24),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Icon(
                        src = AppIcons.Camera,
                        modifier = Modifier.sSize(114).styleModifier {
                            property("filter", AppColors.IconDefaultFilter)
                            property("opacity", "0.3")
                        }
                    )
                    SpanText(
                        t.pageNoPostsYet,
                        modifier = Modifier.h2Medium().color(AppColors.Text.Secondary)
                    )
                }
            } else {
                // Posts list
                Column(modifier = Modifier.fillMaxWidth()) {
                    posts.forEach { post ->
                        ClubPostCard(club = club, post = post, onToggleLike = onToggleLike)
                    }

                    // Load more
                    if (hasMorePosts) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sPadding(12)
                                .cursor(Cursor.Pointer)
                                .onClick { onLoadMore() },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                if (isLoadingPosts) t.pageLoading else t.pageLoadMore,
                                modifier = Modifier.bodyLMedium().color(AppColors.Primary)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ClubPostCard(club: ClubDto?, post: ClubPostDto, onToggleLike: (String) -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .sPadding(16, 12)
            .sGap(12)
    ) {
        // Header: Avatar + Name + Date
        Row(
            modifier = Modifier.fillMaxWidth().sGap(12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Avatar
            Box(
                modifier = Modifier
                    .sSize(40)
                    .sBorderRadius(20)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("flex-shrink", "0")
                        property("background", "rgba(60, 121, 241, 0.2)")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (!club?.mainPhotoUrl.isNullOrBlank()) {
                    Image(
                        src = club!!.mainPhotoUrl!!,
                        modifier = Modifier.fillMaxSize().styleModifier {
                            property("object-fit", "cover")
                        }
                    )
                } else {
                    Icon(
                        src = AppIcons.Profile,
                        modifier = Modifier.sSize(20).styleModifier {
                            property("filter", AppColors.IconDefaultFilter)
                        }
                    )
                }
            }

            // Name + Date
            Column(
                modifier = Modifier.styleModifier {
                    property("flex", "1")
                    property("min-width", "0")
                }
                    .sGap(4)
            ) {
                SpanText(
                    club?.name ?: "",
                    modifier = Modifier.bodyXlMedium().color(AppColors.Text.Primary)
                )
                SpanText(
                    formatDateTime(post.createdAt),
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Subtle)
                )
            }
        }

        // Post content
        if (post.content.isNotBlank()) {
            SpanText(
                post.content,
                modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
            )
        }

        // Post images
        if (post.images.isNotEmpty()) {
            PostImageGrid(images = post.images.map { it.imageUrl })
        }

        // Likes
        Row(
            modifier = Modifier.sGap(8)
                .cursor(Cursor.Pointer)
                .onClick { onToggleLike(post.id) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                src = AppIcons.Heart,
                modifier = Modifier.sSize(24).styleModifier {
                    if (post.isLikedByMe) {
                        property("filter", "brightness(0) saturate(100%) invert(24%) sepia(95%) saturate(6000%) hue-rotate(348deg)")
                    } else {
                        property("filter", AppColors.IconDefaultFilter)
                    }
                }
            )
            SpanText(
                "${post.likesCount}",
                modifier = Modifier
                    .bodyXlRegular()
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}

@Composable
private fun PostImageGrid(images: List<String>) {
    when {
        images.size == 1 -> {
            Image(
                src = images[0],
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(265)
                    .sBorderRadius(12)
                    .styleModifier { property("object-fit", "cover") }
            )
        }

        images.size == 2 -> {
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(5))
                    property("height", s(265))
                    property("width", "100%")
                }
            }) {
                images.forEach { url ->
                    Image(
                        src = url,
                        modifier = Modifier.styleModifier {
                            property("flex", "1")
                            property("min-width", "0")
                            property("height", "100%")
                            property("object-fit", "cover")
                            property("border-radius", s(12))
                        }
                    )
                }
            }
        }

        images.size >= 3 -> {
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(5))
                    property("height", s(265))
                    property("width", "100%")
                }
            }) {
                Image(
                    src = images[0],
                    modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("border-radius", s(12))
                    }
                )
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("min-width", "0")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", s(5))
                    }
                }) {
                    images.drop(1).take(2).forEach { url ->
                        Image(
                            src = url,
                            modifier = Modifier.styleModifier {
                                property("flex", "1")
                                property("min-height", "0")
                                property("width", "100%")
                                property("object-fit", "cover")
                                property("border-radius", s(12))
                            }
                        )
                    }
                }
            }
        }
    }
}
